import React from 'react';
import {
  Building2,
  CheckCircle2,
  Download,
  FileCode2,
  FileText,
  LinkIcon,
  Receipt,
  UserRound,
  X
} from 'lucide-react';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const dateFormatter = new Intl.DateTimeFormat('pt-BR');

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
};

const Detail = ({ label, value, mono = false }) => (
  <div>
    <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className={`mt-1 text-sm text-gray-900 ${mono ? 'break-all font-mono text-xs' : ''}`}>{value || '—'}</dd>
  </div>
);

export default function ImportedNfseViewer({ nfse, invoice, onClose, onOpenLink }) {
  if (!nfse) return null;

  const handleDownloadXML = () => {
    if (!nfse.xml_content) return;
    const blob = new Blob([nfse.xml_content], { type: 'application/xml;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `nfse_${nfse.numero_nfse || nfse.id}.xml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = () => {
    if (!nfse.pdf_base64) return;
    const downloadLink = document.createElement('a');
    downloadLink.href = `data:application/pdf;base64,${nfse.pdf_base64}`;
    downloadLink.download = `nfse_${nfse.numero_nfse || nfse.id}.pdf`;
    downloadLink.click();
  };

  const valueDifference = invoice
    ? Number(invoice.total_amount || 0) - Number(nfse.valor_servico || 0)
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 p-5 sm:p-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="rounded-xl bg-purple-100 p-3 text-purple-700"><Receipt className="h-6 w-6" /></div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900">NFS-e importada</h2>
              <p className="mt-1 text-sm text-gray-500">Número {nfse.numero_nfse || 'não identificado'} · emitida em {formatDate(nfse.data_emissao)}</p>
            </div>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="Fechar"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5 sm:p-6">
          <section className="rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-3"><FileText className="h-5 w-5 text-purple-700" /><h3 className="font-semibold text-gray-900">Dados da nota</h3></div>
            <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Detail label="Número" value={nfse.numero_nfse} />
              <Detail label="Data de emissão" value={formatDate(nfse.data_emissao)} />
              <Detail label="Valor do serviço" value={currencyFormatter.format(Number(nfse.valor_servico || 0))} />
              <Detail label="Data da importação" value={formatDate(nfse.created_at || nfse.imported_at)} />
              <div className="sm:col-span-2 lg:col-span-4"><Detail label="Chave de acesso" value={nfse.chave_acesso} mono /></div>
            </dl>
          </section>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            <section className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3"><Building2 className="h-5 w-5 text-blue-700" /><h3 className="font-semibold text-gray-900">Prestador</h3></div>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Detail label="Nome / razão social" value={nfse.prestador_nome} /></div>
                <Detail label="CNPJ" value={nfse.prestador_cnpj} />
                <Detail label="Município" value={nfse.prestador_municipio || nfse.municipio_prestacao} />
              </dl>
            </section>

            <section className="rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 border-b border-gray-100 pb-3"><UserRound className="h-5 w-5 text-emerald-700" /><h3 className="font-semibold text-gray-900">Tomador</h3></div>
              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2"><Detail label="Nome / razão social" value={nfse.tomador_nome} /></div>
                <Detail label="CPF/CNPJ" value={nfse.tomador_cpf_cnpj} />
                <Detail label="Paciente identificado" value={nfse.patient_name || (nfse.patient_id ? 'Paciente localizado' : 'Não identificado')} />
              </dl>
            </section>
          </div>

          {(nfse.descricao_servico || nfse.service_description || nfse.notes) && (
            <section className="rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900">Serviço e anotações</h3>
              {(nfse.descricao_servico || nfse.service_description) && <p className="mt-3 whitespace-pre-wrap text-sm text-gray-700">{nfse.descricao_servico || nfse.service_description}</p>}
              {nfse.notes && <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm text-gray-600 whitespace-pre-wrap">{nfse.notes}</div>}
            </section>
          )}

          <section className={`rounded-xl border p-4 ${invoice ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <div className="flex items-center gap-2">
                  {invoice ? <CheckCircle2 className="h-5 w-5 text-green-700" /> : <LinkIcon className="h-5 w-5 text-amber-700" />}
                  <h3 className="font-semibold text-gray-900">Vínculo financeiro</h3>
                </div>
                {invoice ? (
                  <div className="mt-3 space-y-1 text-sm text-gray-700">
                    <p><strong>Fatura:</strong> #{invoice.invoice_number}</p>
                    <p><strong>Paciente:</strong> {invoice.patient_name || '—'}</p>
                    <p><strong>Valor da fatura:</strong> {currencyFormatter.format(Number(invoice.total_amount || 0))}</p>
                    <p><strong>Diferença:</strong> {currencyFormatter.format(Math.abs(valueDifference || 0))} {Math.abs(valueDifference || 0) < 0.01 ? '(valores compatíveis)' : ''}</p>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-amber-900">Esta nota ainda não está associada a uma fatura do OdontoAssist.</p>
                )}
              </div>
              <button onClick={() => onOpenLink?.(nfse)} className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
                <LinkIcon className="h-4 w-4" /> {invoice ? 'Trocar vínculo' : 'Vincular à fatura'}
              </button>
            </div>
          </section>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-gray-50 p-5 sm:flex-row sm:justify-end sm:p-6">
          <button onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Fechar</button>
          {nfse.xml_content && <button onClick={handleDownloadXML} className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"><FileCode2 className="h-4 w-4" /><Download className="h-4 w-4" />Baixar XML</button>}
          {nfse.pdf_base64 && <button onClick={handleDownloadPDF} className="inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-semibold text-white hover:bg-purple-700"><FileText className="h-4 w-4" /><Download className="h-4 w-4" />Baixar PDF</button>}
        </div>
      </div>
    </div>
  );
}
